import React from 'react'
import { Box, Divider, Stack, Tab, Tabs } from '@mui/material'
import {
  AccountTreeOutlined,
  CategoryOutlined,
  Inventory2Outlined,
  LayersOutlined,
} from '@mui/icons-material'

import { poolwerxColors } from '../../../../../theme/colors'
import type { CatalogueReviewResponse } from '../../../../../config/tools/productCatalogueReview/types'
import CountPill from './CountPill'

const tabGroupSx = {
  minHeight: 44,
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 600,
    minHeight: 44,
    // A touch tighter than the original (2.25) so the row scans more
    // densely, but not so narrow that each tab feels cramped.
    px: 1.75,
    color: poolwerxColors.grey[700],
    transition: 'color 0.15s ease',
    '&:hover': { color: poolwerxColors.grey[900] },
  },
  '& .Mui-selected': { color: `${poolwerxColors.teal} !important` },
  '& .MuiTabs-indicator': {
    backgroundColor: poolwerxColors.teal,
    height: 2,
  },
} as const

// Setup-group icons sit a shade lighter than the label so the four left
// tabs read as informational (browse / look around) without competing
// with the review-group tabs that only carry a label + count pill.
// Inherits teal on hover/active via the Tab's text-color cascade.
const setupIconSx = {
  fontSize: 16,
  color: poolwerxColors.grey[500],
  '.Mui-selected &': { color: 'inherit' },
} as const

export interface CatalogueTabIndexes {
  overview: 0
  variants: 1
  composites: 2
  categories: 3
  inactive: 4
  dormant: 5
  duplicates: 6
  nonSpl: 7
}

interface CatalogueTabsProps {
  data: CatalogueReviewResponse
  tab: number
  setTab: (next: number) => void
  splOnly: boolean
  indexes: CatalogueTabIndexes
}

const CatalogueTabs: React.FC<CatalogueTabsProps> = ({
  data,
  tab,
  setTab,
  splOnly,
  indexes,
}) => (
  <Box
    sx={{
      borderBottom: 1,
      borderColor: 'divider',
      overflowX: 'auto',
    }}
  >
    <Stack
      direction="row"
      alignItems="stretch"
      sx={{ px: 2, minWidth: 'fit-content' }}
    >
      <Tabs
        value={tab <= indexes.categories ? tab : false}
        onChange={(_, v) => setTab(v as number)}
        sx={tabGroupSx}
      >
        <Tab
          value={indexes.overview}
          label={
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Inventory2Outlined sx={setupIconSx} />
              <span>Overview</span>
            </Stack>
          }
        />
        <Tab
          value={indexes.variants}
          label={
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <AccountTreeOutlined sx={setupIconSx} />
              <span>Variants</span>
              <CountPill
                count={
                  data.summary.counts_by_product_type.variant_parent +
                  data.summary.counts_by_product_type.variant_child
                }
                variant="neutral"
              />
            </Stack>
          }
        />
        <Tab
          value={indexes.composites}
          label={
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <LayersOutlined sx={setupIconSx} />
              <span>Composites</span>
              <CountPill
                count={data.composite_depth.items.length}
                variant="neutral"
              />
            </Stack>
          }
        />
        <Tab
          value={indexes.categories}
          label={
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <CategoryOutlined sx={setupIconSx} />
              <span>Categories</span>
              <CountPill
                count={data.category_alignment_total_count ?? 0}
                variant={
                  (data.category_alignment_action_count ?? 0) > 0
                    ? 'danger'
                    : 'neutral'
                }
              />
            </Stack>
          }
        />
      </Tabs>
      {/* Thin divider between the setup/product group and the
        * review-check group, with generous horizontal margins so the
        * boundary reads even though both groups stay adjacent. */}
      <Divider
        orientation="vertical"
        flexItem
        sx={{
          my: 0.75,
          mx: 3,
          borderColor: poolwerxColors.grey[300],
        }}
      />
      <Tabs
        value={tab >= indexes.inactive ? tab : false}
        onChange={(_, v) => setTab(v as number)}
        sx={tabGroupSx}
      >
        <Tab
          value={indexes.inactive}
          label={
            <Stack direction="row" alignItems="center">
              <span>Inactive In Use</span>
              <CountPill count={data.inactive_in_use.length} variant="danger" />
            </Stack>
          }
        />
        <Tab
          value={indexes.dormant}
          label={
            <Stack direction="row" alignItems="center">
              <span>Dormant</span>
              <CountPill count={data.dormant.total} variant="warning" />
            </Stack>
          }
        />
        <Tab
          value={indexes.duplicates}
          label={
            <Stack direction="row" alignItems="center">
              <span>Potential Duplicates</span>
              <CountPill
                count={data.potential_duplicates_groups ?? 0}
                variant="danger"
              />
            </Stack>
          }
        />
        {!splOnly && (
          <Tab
            value={indexes.nonSpl}
            label={
              <Stack direction="row" alignItems="center">
                <span>Non-PWX Std Prod List</span>
                <CountPill count={data.non_spl.total_non_spl} variant="warning" />
              </Stack>
            }
          />
        )}
      </Tabs>
    </Stack>
  </Box>
)

export default CatalogueTabs
